/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'

import { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { Formik, Form, Field } from 'formik'
import moment from 'moment'
import { Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import ReactSelect from 'react-select'
import * as Yup from 'yup'

import UppercaseMaskInput from 'components/atoms/UppercaseMaskInput'
import { macMask } from 'helpers/masks'
import { TextInput } from 'components/atoms/Input'
import { CheckboxInput } from 'components/atoms/Checkbox'
import { SelectInput } from 'components/atoms/Select'
import { TextAreaInput } from 'components/atoms/Textarea'
import ButtonCancel from 'components/atoms/ButtonCancel'
import ButtonSave from 'components/atoms/ButtonSave'
import ReadOnlyFieldset from 'components/atoms/ReadOnlyFieldset'
import {
  useLazyGetLabDevicesQuery,
  useGetDeviceBrandsQuery,
  useGetLabDeviceQuery,
} from 'store/services/devices'
import { useGetLabOfficesQuery } from 'store/services/office'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from '../user/permissions'
import { DeviceStates, statusOptions, WESPER_BRAND_ID } from './constants'
import { DeviceFormValues } from './DeviceUpsert'

// Derived from the device's check-outs; the device's own actions change them, not this form.
const SHELF_LIFE_FIELDS = [
  'firstCheckedOutAt',
  'expirationDate',
  'usedCount',
  'lastCountedOrderId',
  'accessories',
  'replacedAccessories',
  'shelfLife',
]

const isWesper = (brandId: unknown) => Number(brandId) === WESPER_BRAND_ID

// TES-3237: only a Wesper can stand in for the kits Wesper ships itself; one moved to another
// type stops doing so.
const withWesperDropship = (values: Record<string, unknown>) => {
  if (isWesper(values.brandId)) return { ...values, usedForWesperDropship: !!values.usedForWesperDropship }

  const { usedForWesperDropship, ...rest } = values

  return usedForWesperDropship ? { ...rest, usedForWesperDropship: false } : rest
}

interface IProps {
  id: number | undefined
  onSubmit: (val: any) => void
  allowedDevices?: number[]
  onCancel?: () => void
  submitting?: boolean
  onDelete?: () => void | Promise<void>
  /** When false, "Assigned To" (office) is read-only. */
  canManageDeviceInventory?: boolean
  /** When true, the whole form is view-only (no inputs, no Save/Delete). */
  readOnly?: boolean
}

const TimeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;

  .react-datepicker-wrapper {
    flex: 1;

    input {
      width: 100%;
      padding: 7px 8px;
    }
    svg {
      top: 9px !important;
      right: 7px !important;
    }
    button {
      margin-right: 20px;
    }
  }
`

const CreateDeviceForm = ({
  id,
  allowedDevices,
  onSubmit,
  onCancel,
  onDelete,
  submitting,
  canManageDeviceInventory = true,
  readOnly = false,
}: IProps) => {
  const { data: offices } = useGetLabOfficesQuery({})
  const { data: brands } = useGetDeviceBrandsQuery({ userType: 'LAB' })
  const { data: device } = useGetLabDeviceQuery(id ? Number(id) : skipToken)
  const navigate = useNavigate()
  const [getDevices] = useLazyGetLabDevicesQuery()
  const canOverrideShelfLife = useLabPermission(UserPermission.OVERRIDE_DEVICE_SHELF_LIFE)
  // TES-3191: a retired device goes back into service only by someone allowed to override shelf life
  const statusLocked = device?.state === DeviceStates.RETIRED && !canOverrideShelfLife

  const officesOptions = useMemo(
    () =>
      offices?.map((o) => ({
        value: o.id,
        label: `${o.officeName} (${o.addressCity}, ${o.addressState})`,
      })),
    [offices],
  )

  const isTsDevice = (val) => {
    const brand = brands?.items?.find((b) => b.id === Number(val))
    if (
      brand?.name?.toLowerCase()?.includes('testsmarter') ||
      brand?.name?.toLowerCase()?.includes('viatom') ||
      brand?.name?.toLowerCase()?.includes('wesper') ||
      brand?.name?.toLowerCase()?.includes('philips') ||
      brand?.name?.toLowerCase()?.includes('cadwell') ||
      brand?.name?.toLowerCase()?.includes('clevemed') ||
      brand?.name?.toLowerCase()?.includes('checkmeo2')
    )
      return true
  }

  const formValidation = Yup.object().shape({
    brandId: Yup.string().required('Required'),
    label: Yup.string()
      .required('Required')
      .test('unique-label', 'Label already exists', async function (value) {
        if (this.parent.labelOverride) return true
        const { data } = await getDevices({ label: value })
        const items = data?.items?.find((i) => i.label === value && i.id !== Number(id))
        return !items
      }),
    serial: Yup.string().when('brandId', {
      is: isTsDevice,
      then: Yup.string().required('Required'),
    }),
    mac: Yup.string().when('brandId', {
      is: isTsDevice,
      then: Yup.string().test(
        'len',
        'Must be exactly 17 characters',
        (val) => val?.replace(/_/g, '')?.length === 17,
      ),
    }),
    bluetoothId: Yup.string().when('brandId', {
      is: isTsDevice,
      then: Yup.string().test(
        'len',
        'Must be exactly 17 characters',
        (val) => val?.replace(/_/g, '')?.length === 17,
      ),
    }),
  })

  const typeOptions = useMemo(
    () =>
      brands?.items
        ?.map(({ model, id, name }) => ({
          value: name,
          key: id,
        }))
        .filter(({ key }) => !allowedDevices?.length || allowedDevices.includes(key)) || [],
    [brands],
  )

  const initialValues: DeviceFormValues = useMemo(
    () =>
      id === undefined
        ? {
            brandId: undefined,
            label: '',
            serial: '',
            mac: '',
            bluetoothId: '',
            state: '',
            statusUpdatedAt: '',
            officeId: '',
            enabled: false,
            purchaseCost: null,
            salePrice: null,
            notes: '',
            labelOverride: false,
            availability: 'AVALABLE',
            usedForWesperDropship: false,
          }
        : {
            ...device,
            // devices from before TES-3237 do not carry it
            usedForWesperDropship: !!device?.usedForWesperDropship,
            statusUpdatedAt:
              device?.statusUpdatedAt && device.statusUpdatedAt.toLowerCase() !== 'invalid date'
                ? moment(device?.statusUpdatedAt).toDate()
                : undefined,
          },
    [id, device],
  )

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidation}
      initialValues={initialValues}
      onSubmit={(values) =>
        onSubmit(
          withWesperDropship(
            Object.fromEntries(Object.entries(values).filter(([key]) => !SHELF_LIFE_FIELDS.includes(key))),
          ),
        )
      }>
      {({ values, setFieldValue, handleChange, errors }) => (
        <Form>
          <ReadOnlyFieldset readOnly={readOnly}>
          <div className="form-group">
            <label htmlFor="brandId" className="textGray">
              Type
            </label>
            <Field
              component={SelectInput}
              name="brandId"
              options={typeOptions}
              value={values.brandId}
            />
          </div>
          {isWesper(values.brandId) && (
            <div className="form-group" data-testid="wesper-dropship">
              <Field
                component={CheckboxInput}
                name="usedForWesperDropship"
                label="Used for Wesper Dropship"
                value={values.usedForWesperDropship}
              />
              <small className="text-muted d-block">
                Stands in for the kits Wesper ships itself: no expiration date and no accessories to record.
              </small>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="label" className="textGray">
              Label
            </label>
            <Field component={TextInput} name="label" value={values.label} />
            {!!id && errors?.label === 'Label already exists' && (
              <Field
                component={CheckboxInput}
                name="labelOverride"
                label="Override"
                className="mt-1"
                value={values.labelOverride}
              />
            )}
          </div>
          <div className="form-group">
            <label htmlFor="serial" className="textGray">
              Serial No
            </label>
            <Field component={TextInput} name="serial" value={values.serial} />
          </div>
          <div className="form-group">
            <label htmlFor="mac" className="textGray">
              MAC Addr.
            </label>
            <UppercaseMaskInput
              mask={macMask}
              name="mac"
              value={values.mac ?? ''}
              onChange={handleChange}
              placeholder="##:##:##:##:##:##"
            />
          </div>

          <div className="form-group">
            <label htmlFor="bluetoothId" className="textGray">
              BT ID
            </label>
            <UppercaseMaskInput
              mask={macMask}
              name="bluetoothId"
              value={values.bluetoothId ?? ''}
              onChange={handleChange}
              placeholder="##:##:##:##:##:##"
            />
          </div>
          <div className="form-group">
            <Field
              component={SelectInput}
              label="Status"
              name="state"
              options={statusOptions.map((o) => ({ value: o.key, key: o.value }))}
              value={values.state}
              disabled={statusLocked}
            />
            {statusLocked && (
              <small className="text-muted d-block mt-1" data-testid="retired-status-hint">
                Only users allowed to override shelf life can take a device out of Retired.
              </small>
            )}
          </div>
          <div className="form-group">
            <Field
              component={SelectInput}
              label="Availability"
              name="availability"
              options={['AVAILABLE', 'UNAVAILABLE'].map((o) => ({ value: o, key: o }))}
              value={values.availability}
            />
          </div>
          <div className="form-group">
            <TimeWrapper>
              <label htmlFor="bluetoothId" className="textGray">
                Status Date
              </label>

              <div>
                {values.statusUpdatedAt ? (
                  <div className="d-flex align-items-center">
                    {moment(values.statusUpdatedAt).format('MMMM D, YYYY')}
                  </div>
                ) : (
                  <div className="d-flex align-items-center">
                    <span className="textGray">No Date</span>
                  </div>
                )}
              </div>
              {/* <DateTimePicker
                          value={values.statusUpdatedAt}
                          handleChange={(date) => setFieldValue('statusUpdatedAt', date)}
                          showTimeSelect={false}
                          isClearable
                          dateFormat="MMMM d, yyyy"
                        /> */}
            </TimeWrapper>
          </div>
          <div className="input-group mb-3">
            <label className="textGray">Assigned To</label>
            {canManageDeviceInventory ? (
              <ReactSelect
                className="basic-single w-100"
                css={{ '& .select__control': { height: 34, minHeight: 'unset' } }}
                classNamePrefix="select"
                isClearable
                isSearchable
                placeholder="Select DME Office"
                name="officeId"
                onChange={(item) => setFieldValue('officeId', item?.value)}
                options={officesOptions}
                value={officesOptions?.find((o) => o.value == values.officeId) ?? null}
              />
            ) : (
              <div className="form-control-plaintext">
                {officesOptions?.find((o) => o.value == values.officeId)?.label ?? '—'}
              </div>
            )}
          </div>
          <div className="form-group">
            <Field
              component={TextInput}
              label="Purchase Cost"
              name="purchaseCost"
              type="number"
              value={values.purchaseCost}
            />
          </div>
          <div className="form-group">
            <Field
              component={TextInput}
              label="Sale Price"
              name="salePrice"
              type="number"
              value={values.salePrice}
            />
          </div>
            <Field
              component={TextAreaInput}
              name="notes"
              label="Notes"
              value={values.notes}
              rows={3}
              placeholder="Notes..."
            />
          </ReadOnlyFieldset>
          <div
            style={{
              padding: '0.75rem 10px',
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              borderTop: '0 solid rgba(0, 0, 0, 0.125)',
            }}
            className="text-right d-flex align-items-center justify-content-between">
            <div>
              {onDelete && !readOnly && (
                <Button variant="danger" onClick={onDelete}>
                  Delete
                </Button>
              )}
            </div>
            <div>
              <ButtonCancel
                className="mr-2"
                onClick={
                  onCancel ||
                  (() => {
                    navigate(-1)
                  })
                }
              />{' '}
              {!readOnly && <ButtonSave type="submit" disabled={submitting} />}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default CreateDeviceForm
